/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { RosterPlayerListItem } from '@sportninja/common/features/team/player/mapRosterPlayerToForm'
import type { RosterSearchPlayer } from '@sportninja/common/features/team/player/mapSearchPlayerToForm'
import type { OrganizationDetails } from '@sportninja/common/services/organization/readOrganization'
import WarningModal from 'src/components/WarningModal'
import RosterPlayerModal from 'src/features/team/player/RosterPlayerModal'
import PlayerSearchModal from 'src/features/team/player/components/PlayerSearchModal'
import FamilyManagementModal from 'src/features/profile/family/FamilyManagementModal'
import TeamOfficialModal from 'src/components/TeamOfficialModal'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'

const translate = t as (key: string, options?: unknown) => string

/**
 * A roster row as the context holds it. Players carry the full
 * `RosterPlayerListItem` shape the edit modal needs; officials are looser, so
 * both are kept indexable for the shared name/slug helpers.
 */
type RawPlayer = RosterPlayerListItem & Record<string, any>
type Raw = Record<string, any>

export type RosterModalState =
  | { kind: 'none' }
  | { kind: 'player-search' }
  // `searchPlayer` set → add-existing (prefilled, copy endpoint); absent → create-new.
  | { kind: 'player-add'; searchPlayer?: RosterSearchPlayer }
  | { kind: 'player-edit'; player: RawPlayer }
  | { kind: 'player-resend'; player: RawPlayer }
  | { kind: 'player-family'; player: RawPlayer }
  | { kind: 'player-remove'; player: RawPlayer }
  | { kind: 'official-add' }
  | { kind: 'official-edit'; official: Raw }
  | { kind: 'official-resend'; official: Raw }
  | { kind: 'official-remove'; official: Raw }

interface RosterModalsProps {
  state: RosterModalState
  teamId: string
  /** Shown in the search modal's title: "Add Player - {teamName}". */
  teamName: string
  rosterId: string
  sportId: string | null
  canAdmin: boolean
  /** Scopes the player search (org id, plus account id for Global Search). */
  organization: OrganizationDetails | null
  /** Advances a multi-step flow — the player search hands off to the add form. */
  onStateChange: (next: RosterModalState) => void
  onClose: () => void
  /**
   * The full-screen shell's layer while the tab is full screen, else absent.
   * Forwarded to the roster modals so they stack above the shell.
   */
  hostZIndex?: number
}

const nameOf = (member: Raw): string =>
  [member?.name_first, member?.name_last].filter(Boolean).join(' ') ||
  member?.name_full ||
  ''

/**
 * Hosts every mutating roster flow for the Operations panel.
 *
 * Nothing here is reimplemented: Add Player opens `PlayerSearchModal` first and
 * hands off to `RosterPlayerModal` (the Team screen's two-step flow), add/edit
 * reuse `RosterPlayerModal` and `TeamOfficialModal`, resend hits the same
 * `/invitations/:id/resend` endpoint as `InviteAction`, and remove is a DELETE
 * on the member's roster slug behind the tab's own red confirmation.
 */
const RosterModals = ({
  state,
  teamId,
  teamName,
  rosterId,
  sportId,
  canAdmin,
  organization,
  onStateChange,
  onClose,
  hostZIndex,
}: RosterModalsProps) => {
  'use memo'

  const { message } = App.useApp()
  const { refresh, requireEmail, players, affiliates } = useRosterContext()
  const [isWorking, setIsWorking] = useState(false)

  // Everyone already on the roster, spares included — filtered out of the
  // search so a player cannot be added twice. Matches the Team screen.
  const rosterPlayerIds: string[] = [...(players ?? []), ...(affiliates ?? [])]
    .map((player: { id?: string }) => player.id)
    .filter((id): id is string => Boolean(id))

  const isPlayer = state.kind.startsWith('player-')
  const member =
    'player' in state
      ? state.player
      : 'official' in state
      ? state.official
      : null

  const slugFor = (item: Raw): string =>
    isPlayer
      ? `/teams/${teamId}/rosters/${rosterId}/players/${item.id}`
      : `/teams/${teamId}/rosters/${rosterId}/team-officials/${item.id}`

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setIsWorking(true)
    try {
      await action()
      await refresh()
      onClose()
    } catch (error) {
      message.error(error instanceof Error ? error.message : failure)
    } finally {
      setIsWorking(false)
    }
  }

  const handleResend = () =>
    run(
      () =>
        req(
          // Falls back to the member id, matching InviteAction's own rule.
          `/invitations/${member?.invitation_id || member?.id}/resend`
        ),
      'Could not resend the invitation'
    )

  const handleRemove = () =>
    run(
      () => req(slugFor(member as Raw), { method: 'DELETE' }),
      'Could not remove this member'
    )

  if (state.kind === 'player-search') {
    return (
      <PlayerSearchModal
        isOpen
        onClose={onClose}
        organizationId={organization?.id}
        accountId={organization?.account_id}
        teamName={teamName}
        excludePlayerIds={rosterPlayerIds}
        onSelectExisting={(player) =>
          onStateChange({ kind: 'player-add', searchPlayer: player })
        }
        onCreateNew={() => onStateChange({ kind: 'player-add' })}
        hostZIndex={hostZIndex}
      />
    )
  }

  if (state.kind === 'player-add' || state.kind === 'player-edit') {
    const searchPlayer =
      state.kind === 'player-add' ? state.searchPlayer : undefined
    return (
      <RosterPlayerModal
        isOpen
        editPlayer={state.kind === 'player-edit' ? state.player : undefined}
        playerId={searchPlayer?.id}
        searchPlayer={searchPlayer}
        onClose={onClose}
        onSuccess={async () => {
          await refresh()
          onClose()
        }}
        teamId={teamId}
        rosterId={rosterId}
        sportId={sportId}
        hasAdminPermission={canAdmin}
        requireEmail={requireEmail}
        hostZIndex={hostZIndex}
      />
    )
  }

  if (state.kind === 'official-add' || state.kind === 'official-edit') {
    return (
      <TeamOfficialModal
        isOpen
        // No `initialData` puts the modal in create mode — it derives `isEdit`
        // from the presence of an id.
        initialData={
          state.kind === 'official-edit' ? state.official : undefined
        }
        onClose={onClose}
        onSuccess={async () => {
          await refresh()
          onClose()
        }}
        teamId={teamId}
        rosterId={rosterId}
        sportId={sportId}
      />
    )
  }

  // Reached only from the admin-gated menu item, so `canManage` is always true
  // here. The roster scope routes every call through the team-admin endpoints
  // rather than the self-scoped ones, and passing the raw row as `player` lets
  // the contact modals render their card from data already in hand instead of
  // re-fetching a player this caller does not own — matching the Team screen.
  if (state.kind === 'player-family') {
    return (
      <FamilyManagementModal
        isOpen
        playerId={state.player.id ?? null}
        onClose={onClose}
        canManage
        scope={{ kind: 'roster', teamId, rosterId }}
        player={state.player}
      />
    )
  }

  if (state.kind === 'player-resend' || state.kind === 'official-resend') {
    return (
      <WarningModal
        isOpen
        isLoading={isWorking}
        onClose={onClose}
        onConfirm={handleResend}
        title={capitalizeWords(translate('common:resendInvite'))}
        description={translate('common:resendInviteDescription', {
          name: nameOf(member as Raw),
        })}
        cancelLabel={translate('common:cancel').toUpperCase()}
        confirmLabel={translate('common:confirm').toUpperCase()}
      />
    )
  }

  if (state.kind === 'player-remove' || state.kind === 'official-remove') {
    return (
      <WarningModal
        isOpen
        destructive
        isLoading={isWorking}
        onClose={onClose}
        onConfirm={handleRemove}
        title={capitalizeWords(
          translate('common:removeEntity', {
            entity: translate(
              isPlayer ? 'common:player' : 'common:teamOfficial'
            ),
          })
        )}
        description={translate('common:removeNamedEntityDescription', {
          name: nameOf(member as Raw),
        })}
        cancelLabel={translate('common:cancel').toUpperCase()}
        confirmLabel={translate('common:confirm').toUpperCase()}
      />
    )
  }

  return null
}

export default RosterModals
